import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { cn } from '../../utils/cn';
import { Button } from './Button';
import type { Theme } from '../../contexts/ThemeContext';

interface ThemeSwitcherProps {
  variant?: 'button' | 'dropdown' | 'toggle' | 'mobile';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

export const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({
  variant = 'dropdown',
  size = 'md',
  showLabel = true,
  className
}) => {
  const { theme, actualTheme, setTheme, toggleTheme, isDark } = useTheme();

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-2',
    lg: 'text-base px-4 py-3'
  };

  const iconSize = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  // Icons
  const SunIcon = () => (
    <svg className={iconSize[size]} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );

  const MoonIcon = () => (
    <svg className={iconSize[size]} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  );

  const SystemIcon = () => (
    <svg className={iconSize[size]} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );

  const getThemeIcon = (themeValue: Theme) => {
    switch (themeValue) {
      case 'light':
        return <SunIcon />;
      case 'dark':
        return <MoonIcon />;
      case 'system':
        return <SystemIcon />;
      default:
        return <SunIcon />;
    }
  };

  const getThemeLabel = (themeValue: Theme) => {
    switch (themeValue) {
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      case 'system':
        return 'System';
      default:
        return 'Light';
    }
  };

  if (variant === 'mobile') {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleTheme}
        className={cn(
          'rounded-full p-2 text-white',
          isDark ? 'hover:bg-white/15' : 'hover:bg-white/10',
          className
        )}
        title={`Switch to ${actualTheme === 'dark' ? 'light' : 'dark'} mode`}
        aria-label={`Switch to ${actualTheme === 'dark' ? 'light' : 'dark'} mode`}
      >
        {actualTheme === 'dark' ? (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </Button>
    );
  }

  if (variant === 'toggle') {
    return (
      <button
        onClick={toggleTheme}
        className={cn(
          "inline-flex items-center gap-2 rounded-lg border transition-all duration-200",
          "hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50",
          isDark
            ? "bg-secondary-800 border-secondary-700 text-secondary-200 hover:bg-secondary-700"
            : "bg-white border-secondary-300 text-secondary-700 hover:bg-secondary-50",
          sizeClasses[size],
          className
        )}
        title={`Switch to ${actualTheme === 'dark' ? 'light' : 'dark'} mode`}
      >
        {getThemeIcon(actualTheme === 'dark' ? 'light' : 'dark')}
        {showLabel && (
          <span>{actualTheme === 'dark' ? 'Light' : 'Dark'}</span>
        )}
      </button>
    );
  }

  if (variant === 'button') {
    return (
      <div className={cn("flex gap-1 p-1 rounded-lg", isDark ? "bg-secondary-800" : "bg-secondary-100", className)}>
        {(['light', 'dark', 'system'] as Theme[]).map((themeOption) => (
          <button
            key={themeOption}
            onClick={() => setTheme(themeOption)}
            className={cn(
              "inline-flex items-center gap-2 rounded-md transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50",
              sizeClasses[size],
              theme === themeOption
                ? isDark
                  ? "bg-secondary-700 text-white shadow-sm"
                  : "bg-white text-secondary-900 shadow-sm"
                : isDark
                  ? "text-secondary-300 hover:text-white hover:bg-secondary-700"
                  : "text-secondary-600 hover:text-secondary-900 hover:bg-white"
            )}
            title={`Switch to ${getThemeLabel(themeOption).toLowerCase()} mode`}
          >
            {getThemeIcon(themeOption)}
            {showLabel && <span>{getThemeLabel(themeOption)}</span>}
          </button>
        ))}
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div className={cn("relative", className)}>
      <select
        value={theme}
        onChange={(e) => setTheme(e.target.value as Theme)}
        className={cn(
          "appearance-none rounded-lg border transition-all duration-200 pr-8",
          "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50",
          isDark
            ? "bg-secondary-800 border-secondary-700 text-secondary-200"
            : "bg-white border-secondary-300 text-secondary-700",
          sizeClasses[size]
        )}
        aria-label="Theme selection"
        role="combobox"
      >
        <option value="light">☀️ Light</option>
        <option value="dark">🌙 Dark</option>
        <option value="system">💻 System</option>
      </select>

      {/* Custom dropdown arrow */}
      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
        <svg className={cn(iconSize[size], isDark ? "text-secondary-400" : "text-secondary-500")} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

interface ThemeStatusProps {
  showDetails?: boolean;
  className?: string;
}

export const ThemeStatus: React.FC<ThemeStatusProps> = ({
  showDetails = false,
  className
}) => {
  const { isDark, theme } = useTheme();

  return (
    <div className={cn(
      "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm",
      isDark ? "bg-secondary-800 text-secondary-200" : "bg-secondary-100 text-secondary-700",
      className
    )}>
      <div className={cn(
        "w-2 h-2 rounded-full",
        isDark ? "bg-blue-400" : "bg-yellow-500"
      )} />

      <span className="font-medium">
        {isDark ? 'Dark' : 'Light'} Mode
      </span>

      {showDetails && theme === 'system' && (
        <span className="text-xs opacity-75">
          (System)
        </span>
      )}
    </div>
  );
};

export default ThemeSwitcher;